<template>
  <div class="column relative-position">
    <gmap-map
      v-if="wrapperReady"
      v-bind="vAttrs"
      ref="map"
      :center="center"
      :options="{styles}"
      class="col"
      v-on="$listeners"
    >
      <slot />
      <gmap-circle
        v-if="vSite"
        v-bind="vSite"
      />
      <gmap-marker
        v-if="userLocation"
        v-bind="userLocation"
      />
      <gmap-circle
        v-if="userLocation && userLocation.accuracy"
        v-bind="userLocation.accuracy"
      />
    </gmap-map>
    <div
      v-if="!readonly"
      class="absolute-bottom-left q-pl-sm q-pb-md q-gutter-xs"
    >
      <q-btn
        round
        icon="fa fa-crosshairs"
        dense
        color="white"
        text-color="grey"
        @click="onCenter"
      />
      <slot name="tools" />
    </div>
  </div>
</template>

<script>
import { getLocation, lwfToGmap, siteToGmapCircle } from '@/utils/io/geolocation'
import { DEFAULT_LOCATION_MARKER_ICON, MAP_ACCURACY_CIRCLE_OPTIONS } from '@/consts/app'
import propsReadonly from '@/mixins/propsReadonly'

export default {
  name: 'LwfMap',
  mixins: [propsReadonly],
  inheritAttrs: false,
  props: {
    center: {
      type: Object,
      default: undefined
    },
    realtimePosition: Boolean,
    site: {
      type: Object,
      default: undefined
    }
  },
  data () {
    return {
      userLocation: null,
      wrapperReady: false // Fix pour attendre la fin de l'animation des modals
    }
  },
  computed: {
    vAttrs () {
      return {
        zoom: 18,
        ...this.$attrs
      }
    },
    vSite () {
      return siteToGmapCircle(this.site)
    },
    styles () { // Dark mode
      return this.$q.dark.isActive
        ? [
          { elementType: 'geometry', stylers: [{ color: '#242f3e' }] },
          { elementType: 'labels.text.stroke', stylers: [{ color: '#242f3e' }] },
          { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
          {
            featureType: 'administrative.locality',
            elementType: 'labels.text.fill',
            stylers: [{ color: '#d59563' }]
          },
          {
            featureType: 'poi',
            elementType: 'labels.text.fill',
            stylers: [{ color: '#d59563' }]
          },
          {
            featureType: 'poi.park',
            elementType: 'geometry',
            stylers: [{ color: '#263c3f' }]
          },
          {
            featureType: 'poi.park',
            elementType: 'labels.text.fill',
            stylers: [{ color: '#6b9a76' }]
          },
          {
            featureType: 'road',
            elementType: 'geometry',
            stylers: [{ color: '#38414e' }]
          },
          {
            featureType: 'road',
            elementType: 'geometry.stroke',
            stylers: [{ color: '#212a37' }]
          },
          {
            featureType: 'road',
            elementType: 'labels.text.fill',
            stylers: [{ color: '#9ca5b3' }]
          },
          {
            featureType: 'road.highway',
            elementType: 'geometry',
            stylers: [{ color: '#746855' }]
          },
          {
            featureType: 'road.highway',
            elementType: 'geometry.stroke',
            stylers: [{ color: '#1f2835' }]
          },
          {
            featureType: 'road.highway',
            elementType: 'labels.text.fill',
            stylers: [{ color: '#f3d19c' }]
          },
          {
            featureType: 'transit',
            elementType: 'geometry',
            stylers: [{ color: '#2f3948' }]
          },
          {
            featureType: 'transit.station',
            elementType: 'labels.text.fill',
            stylers: [{ color: '#d59563' }]
          },
          {
            featureType: 'water',
            elementType: 'geometry',
            stylers: [{ color: '#17263c' }]
          },
          {
            featureType: 'water',
            elementType: 'labels.text.fill',
            stylers: [{ color: '#515c6d' }]
          },
          {
            featureType: 'water',
            elementType: 'labels.text.stroke',
            stylers: [{ color: '#17263c' }]
          }
        ]
        : []
    }
  },
  watch: {
    realtimePosition: {
      immediate: true,
      handler (v) {
        if (v) {
          navigator.geolocation.clearWatch(this.watchId)
          this.watchId = navigator.geolocation.watchPosition(
            ({ coords: { latitude, longitude, accuracy } }) => {
              const position = {
                lat: latitude,
                lng: longitude
              }
              this.userLocation = {
                position,
                icon: DEFAULT_LOCATION_MARKER_ICON,
                accuracy: accuracy < 500 // Désactive l'affichage de la précision en cas de valeur absurde
                  ? {
                    center: position,
                    radius: accuracy,
                    options: MAP_ACCURACY_CIRCLE_OPTIONS
                  }
                  : null
              }
            },
            console.error,
            {
              enableHighAccuracy: true
            })
        }
      }
    }
  },
  created () {
    setTimeout(async () => {
      this.wrapperReady = true
      await this.$nextTick()
      this.$emit('mounted', this.$refs.map)
    }, 250)
  },
  destroyed () {
    navigator.geolocation.clearWatch(this.watchId)
  },
  methods: {
    async onCenter () {
      const gmapLocation = lwfToGmap(await getLocation())
      this.$emit('update:center', gmapLocation)
      this.$refs.map.panTo(gmapLocation)
    }
  }
}
</script>

<style scoped>

</style>
